import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";

import QueryData from "Constants/QueryData";
import { STATIONS } from "Constants/Stations";

import useEditPath from "Hooks/useEditPath";
import usePaths from "Hooks/usePaths";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import { setPath } from "Redux/singleLocationSlice";

import { changePathsFilter } from "Redux/filtersSlice";
import { StationType } from "Types/station";

import Api from "Api";

import ConfirmModal from "Components/ConfirmModal";

import Header from "./Header";
import Table, { StyledTableCell } from "./Table";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function Paths() {
  const dispatch = useAppDispatch();

  const queryClient = useQueryClient();
  const { editPath, showPathOnMap } = useEditPath();

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [pathId, setPathId] = useState<number | null>(null);
  const { paths } = useAppSelector((state) => state.filters);

  const { data, isLoading, refetch, isFetching } = usePaths(1000);

  const deletePath = useMutation({
    mutationFn: () => {
      return Api.delete(`path/${pathId}`).then((res) => {
        toast.success("مسیر با موفقیت حذف شد");
        setShowConfirm(false);
        queryClient.invalidateQueries({ queryKey: [QueryData.PATH.key] });
      });
    },
  });

  const handleShowPath = (path: any) => {
    dispatch(setPath([path]));
    showPathOnMap(path);
  };

  const goForDelete = (id: number) => {
    setShowConfirm(true);
    setPathId(id);
  };

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    dispatch(changePathsFilter({ type: typeof value === "string" ? value.split(",") : value }));
  };

  const rowContent = (_index: number) => {
    if (!data) return;

    const path = data[_index];

    if (!path) return null;

    const stationData = STATIONS.filter((i) => i.type === path.type)[0];

    return (
      <>
        <StyledTableCell className="fa-number">{_index + 1}</StyledTableCell>
        <StyledTableCell>{path.name}</StyledTableCell>
        <StyledTableCell>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: "center" }}>
            <Icon fontSize="small">{stationData.icon}</Icon>
            <Typography>{stationData.text}</Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <IconButton color="primary" onClick={() => handleShowPath(path)}>
            <LocationOnIcon />
          </IconButton>
        </StyledTableCell>
        <StyledTableCell>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
            <IconButton color="black" onClick={() => editPath(path)}>
              <EditIcon />
            </IconButton>
            <IconButton color="error" onClick={() => goForDelete(path.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </StyledTableCell>
      </>
    );
  };
  const handleChangeFilters = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    dispatch(changePathsFilter({ [name]: value }));
  };

  return (
    <>
      <ConfirmModal open={showConfirm} setOpen={setShowConfirm} message="مسیر انتخاب شده حذف خواهد شد." mutation={deletePath} />

      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Header>
          <Stack sx={{ flexGrow: 1 }} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h4" sx={{ fontWeight: "800" }}>
              لیست مسیرها
            </Typography>

            <Typography sx={{ fontWeight: "500" }} className="fa-number">
              تعداد مسیرها: {data?.length || 0}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button variant="white" size="large">
                <LocalPrintshopOutlinedIcon />
              </Button>
              <Button variant="white" size="large">
                <SystemUpdateAltOutlinedIcon />
              </Button>
            </Stack>
          </Stack>
        </Header>

        <Grid container spacing={1} mb={2}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth label="نام مسیر" name="q" onChange={handleChangeFilters} />
          </Grid>

          <Grid item xs={12} md={2.3}>
            {/* <TextField fullWidth label="نوع مسیر" select name="type" onChange={handleChangeFilters}>
              <MenuItem value="">همه</MenuItem>
              {STATIONS.map((station) => (
                <MenuItem value={station.type}>{station.text}</MenuItem>
              ))}
            </TextField> */}
            <FormControl sx={{ width: "100%" }}>
              <InputLabel id="multiple-station-label">نوع مسیر</InputLabel>
              <Select
                name="type"
                labelId="multiple-station-label"
                id="multiple-station"
                multiple
                value={paths.type}
                onChange={handleChange}
                input={<OutlinedInput label="Name" />}
                // MenuProps={MenuProps}
              >
                {STATIONS.map((station) => (
                  <MenuItem value={station.type}>{station.text}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <LoadingButton fullWidth variant="contained" size="large" onClick={() => refetch()} loading={isFetching}>
              فیلتر
            </LoadingButton>
          </Grid>
        </Grid>

        <Table
          headerColumns={[
            { title: "ردیف", style: {} },
            { title: "نام مسیر", style: {} },
            { title: "نوع مسیر", style: {} },
            { title: "موقعیت مکانی", style: {} },
            { title: "عملیات", style: {} },
          ]}
          isLoading={isLoading}
          data={data}
          rowContent={rowContent}
        />
      </Box>
    </>
  );
}
